import { ORGANISATION_EMAIL_DOMAINS_MAX, ORGANISATION_KIND_VALUES, ORGANISATION_NAME_MAX, ORG_ROLE_VALUES, type OrganisationKind, type OrgRole } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Field, Input, Select } from "@sagot/ui";
import type { ConvertForm, ConvertProblem } from "./lead-convert-model";

interface FieldsProps {
  form: ConvertForm;
  problems: readonly ConvertProblem[];
  onChange: (changes: Partial<ConvertForm>) => void;
}

/** What the lead becomes: the organisation (the lead has no company, so its name is asked for) and the contact, prefilled from the lead. */
export function LeadConvertFields({ form, problems, onChange }: FieldsProps) {
  const { t } = useTranslation();
  return (
    <div className="space-y-5">
      <fieldset className="space-y-4">
        <legend className="text-xs font-bold uppercase tracking-wide text-fg-subtle">{t("service.leadConvert.fields.organisation")}</legend>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("service.leadConvert.fields.organisationName")} hint={t("service.leadConvert.fields.organisationNameHint")} error={problems.includes("organisationName") ? t("service.leadConvert.problems.organisationName") : undefined} className="sm:col-span-2">
            <Input value={form.organisationName} maxLength={ORGANISATION_NAME_MAX} autoComplete="off" onChange={(event) => { onChange({ organisationName: event.target.value }); }} data-testid="lead-org-name" />
          </Field>
          <Field label={t("service.leadConvert.fields.kind")}>
            <Select value={form.kind} onChange={(event) => { onChange({ kind: ORGANISATION_KIND_VALUES.find((kind) => kind === event.target.value) ?? form.kind }); }}>
              {ORGANISATION_KIND_VALUES.map((kind: OrganisationKind) => (
                <option key={kind} value={kind}>
                  {t(`service.org.kind.${kind}`)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t("service.leadConvert.fields.domains")} hint={t("service.leadConvert.fields.domainsHint", { max: ORGANISATION_EMAIL_DOMAINS_MAX })} error={problems.includes("domains") ? t("service.leadConvert.problems.domains") : undefined}>
            <Input value={form.domains} autoComplete="off" placeholder="example.com.ph" onChange={(event) => { onChange({ domains: event.target.value }); }} />
          </Field>
        </div>
      </fieldset>
      <fieldset className="space-y-4">
        <legend className="text-xs font-bold uppercase tracking-wide text-fg-subtle">{t("service.leadConvert.fields.contact")}</legend>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("service.leadConvert.fields.contactName")} error={problems.includes("contactName") ? t("service.leadConvert.problems.contactName") : undefined}>
            <Input value={form.contactName} autoComplete="off" onChange={(event) => { onChange({ contactName: event.target.value }); }} />
          </Field>
          <Field label={t("service.leadConvert.fields.contactEmail")} hint={t("service.leadConvert.fields.contactEmailHint")} error={problems.includes("contactEmail") ? t("service.leadConvert.problems.contactEmail") : undefined}>
            <Input type="email" value={form.contactEmail} autoComplete="off" onChange={(event) => { onChange({ contactEmail: event.target.value }); }} />
          </Field>
          <Field label={t("service.leadConvert.fields.jobTitle")}>
            <Input value={form.jobTitle} autoComplete="off" onChange={(event) => { onChange({ jobTitle: event.target.value }); }} />
          </Field>
          <Field label={t("service.leadConvert.fields.orgRole")}>
            <Select value={form.orgRole} onChange={(event) => { onChange({ orgRole: ORG_ROLE_VALUES.find((role) => role === event.target.value) ?? form.orgRole }); }}>
              {ORG_ROLE_VALUES.map((role: OrgRole) => (
                <option key={role} value={role}>
                  {t(`service.leadConvert.fields.role.${role}`)}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      </fieldset>
    </div>
  );
}
